import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { StatusBadge, DemoBadge } from "@/components/billing/StatusBadge";
import { useGuarded, inputCls, fmtDate, Stat, SubNav } from "@/components/billing/shared";
import { ProductsManager } from "@/components/billing/ProductsManager";

function Breakdown({ title, data }) {
  const rows = Object.entries(data || {});
  return (
    <div className="rounded-xl border border-white/10 p-4">
      <p className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500 mb-2">{title}</p>
      {rows.length === 0 ? <p className="text-sm text-zinc-600">No revenue</p> : rows.map(([k, v]) => <p key={k} className="text-sm flex justify-between"><span>{k}</span><b>${v}</b></p>)}
    </div>
  );
}

function Overview() {
  const [range, setRange] = useState("30d");
  const [demo, setDemo] = useState(false);
  const [cr, setCr] = useState({ start: "", end: "" });
  const [o, setO] = useState(null);
  useEffect(() => {
    if (range === "custom" && !(cr.start && cr.end)) return;
    client.get("/admin/billing/overview", { params: { range, demo, ...cr } }).then((r) => setO(r.data));
  }, [range, demo, cr]);
  if (!o) return null;
  const c = o.current, p = o.previous;
  return (
    <div data-testid="billing-overview">
      <div className="flex flex-wrap gap-2 items-center mb-5">
        <select data-testid="billing-range" className={inputCls} value={range} onChange={(e) => setRange(e.target.value)}>
          {[["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["month", "This month"], ["last_month", "Last month"], ["custom", "Custom"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {range === "custom" && <><input type="date" className={inputCls} value={cr.start} onChange={(e) => setCr({ ...cr, start: e.target.value })} /><input type="date" className={inputCls} value={cr.end} onChange={(e) => setCr({ ...cr, end: e.target.value })} /></>}
        <label className="flex items-center gap-2 text-xs text-zinc-400 ml-auto">Show DEMO data<Switch data-testid="billing-demo-toggle" checked={demo} onCheckedChange={setDemo} /></label>
      </div>
      {demo && <p className="mb-4"><DemoBadge /> <span className="text-xs text-zinc-500">Simulated records, kept separate from real revenue.</span></p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Successful payments" value={c.payments} prev={p.payments} testid="ov-payments" />
        <Stat label="Gross volume (USD)" value={`$${c.gross_usd}`} prev={`$${p.gross_usd}`} testid="ov-gross" />
        <Stat label="Provider fees" value="n/a" testid="ov-fees" />
        <Stat label="Net settled (USDT outcome)" value={c.net_outcome_usdt} testid="ov-net" />
        <Stat label="Active subscriptions" value={o.active_subscriptions} testid="ov-active" />
        <Stat label="New subscribers" value={c.new_subscribers} prev={p.new_subscribers} testid="ov-new" />
        <Stat label="Renewals" value={c.renewals} prev={p.renewals} testid="ov-renewals" />
        <Stat label="Expired subscriptions" value={o.expired_subscriptions} testid="ov-expired" />
        <Stat label="Failed / expired payments" value={c.failed} prev={p.failed} testid="ov-failed" />
        <Stat label="Pending payments" value={o.pending_payments} testid="ov-pending" />
        <Stat label="Needs review" value={c.review} testid="ov-review" />
        <Stat label="QRIS revenue (IDR, separate)" value={`Rp ${Number(o.midtrans_paid_idr).toLocaleString()}`} testid="ov-idr" />
      </div>
      <p className="text-[11px] text-zinc-600 mt-2">{o.provider_fees_note} Pending orders are never counted as revenue.</p>
      <div className="grid md:grid-cols-3 gap-3 mt-5">
        <Breakdown title="Revenue by product" data={c.by.product} />
        <Breakdown title="Revenue by analyst" data={c.by.analyst} />
        <Breakdown title="Revenue by network" data={c.by.network} />
      </div>
    </div>
  );
}

function OrderDetail({ id, onClose }) {
  const [o, setO] = useState(null);
  const [run, dialog] = useGuarded();
  const [reason, setReason] = useState("");
  const load = () => client.get(`/admin/billing/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!o) return null;
  const review = (resolution) => run(async () => { await client.post(`/admin/billing/orders/${id}/review`, { resolution, reason }); toast.success("Review saved"); load(); });
  return (
    <div className="rounded-xl border border-white/15 bg-[#0b0b0b] p-5 mb-5" data-testid="order-detail">
      <div className="flex justify-between"><p className="font-mono-cl text-sm">{o.id}</p><button onClick={onClose} className="text-xs text-zinc-400">Close</button></div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
        <span>Status: <StatusBadge status={o.status} /> {o.is_demo && <DemoBadge />}</span><span>Provider status: {o.provider_status}</span>
        <span>Price: ${o.price_usd}</span><span>Quoted: {o.pay_amount} {o.pay_currency}</span>
        <span>Received: {o.actually_paid || "0"}</span><span>Outcome: {o.outcome_amount || "-"} {o.outcome_currency}</span>
        <span>Payment ID: {o.provider_payment_id}</span><span>Paid: {fmtDate(o.paid_at)}</span>
        {o.review_reason && <span className="text-amber-300 md:col-span-2">Review reason: {o.review_reason}</span>}
      </div>
      {!o.is_demo && o.provider_payment_id && <button data-testid="order-sync" onClick={() => run(async () => { await client.post(`/admin/billing/orders/${id}/sync`); load(); })} className="mt-3 px-4 py-1.5 rounded-full border border-white/15 text-xs">Sync with NOWPayments</button>}
      {o.status === "REVIEW_REQUIRED" && (
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <input data-testid="review-reason" className={inputCls + " flex-1 min-w-[200px]"} placeholder="Decision reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button data-testid="review-no-access" onClick={() => review("resolved_no_access")} className="px-3 py-2 rounded-full border border-white/15 text-xs">Resolve (no access)</button>
          <button data-testid="review-refund" onClick={() => review("refund_case")} className="px-3 py-2 rounded-full border border-amber-300/40 text-amber-300 text-xs">Open refund case</button>
        </div>
      )}
      <p className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500 mt-5 mb-1">Event log</p>
      {o.events.map((e) => <p key={e.id} className="text-xs text-zinc-400">{fmtDate(e.created_at)} · {e.event} · {e.source} · provider {e.provider_status || "-"}</p>)}
      {dialog}
    </div>
  );
}

function Orders({ preset = {} }) {
  const [f, setF] = useState({ status: "", network: "", demo: "", q: "", ...preset });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { client.get("/admin/billing/orders", { params: f }).then((r) => setRows(r.data)); }, [f]);
  return (
    <div data-testid="billing-orders">
      {open && <OrderDetail id={open} onClose={() => setOpen(null)} />}
      <div className="flex flex-wrap gap-2 mb-3">
        <input data-testid="orders-search" className={inputCls + " flex-1"} placeholder="Order ID, payment ID or user ID" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        <select data-testid="orders-status" className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          <option value="">All statuses</option>{["AWAITING_PAYMENT", "CONFIRMING", "PARTIALLY_PAID", "PAID", "EXPIRED", "FAILED", "REFUNDED", "REVIEW_REQUIRED"].map((s) => <option key={s}>{s}</option>)}</select>
        <select data-testid="orders-demo" className={inputCls} value={f.demo} onChange={(e) => setF({ ...f, demo: e.target.value })}><option value="">Live + demo</option><option value="no">Live only</option><option value="yes">Demo only</option></select>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500"><tr>{["Created", "Member", "Plan", "USD", "Crypto", "Network", "Status"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((o) => (
            <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setOpen(o.id)} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer">
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(o.created_at)}</td><td className="px-3 py-2">{o.member}</td>
              <td className="px-3 py-2">{o.product_snapshot.name} {o.is_demo && <DemoBadge />}</td><td className="px-3 py-2">${o.price_usd}</td>
              <td className="px-3 py-2 whitespace-nowrap">{o.actually_paid || 0} / {o.pay_amount}</td><td className="px-3 py-2">{o.network_name}</td>
              <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
            </tr>))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-zinc-500">No orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Subscribers({ expiring }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  useEffect(() => { client.get("/admin/billing/subscriptions", { params: expiring ? { expiring_days: 14 } : { status } }).then((r) => setRows(r.data)); }, [status, expiring]);
  return (
    <div data-testid={expiring ? "billing-renewals" : "billing-subscribers"}>
      {!expiring && <select data-testid="subs-status" className={inputCls + " mb-3"} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All</option>{["ACTIVE", "GRACE_PERIOD", "EXPIRED", "CANCELLED", "SUSPENDED"].map((s) => <option key={s}>{s}</option>)}</select>}
      {expiring && <p className="text-sm text-zinc-400 mb-3">Active subscriptions expiring in the next 14 days. Reminders go out automatically 7, 3 and 1 day before expiry.</p>}
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm"><thead className="text-[10px] font-mono-cl uppercase tracking-[0.14em] text-zinc-500"><tr>{["Member", "Plan", "Source", "Status", "Started", "Expires", "Periods"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} className="border-t border-white/5"><td className="px-3 py-2">{s.member}</td><td className="px-3 py-2">{s.product_name}</td><td className="px-3 py-2">{s.source}</td>
              <td className="px-3 py-2"><StatusBadge status={s.status} /></td><td className="px-3 py-2">{fmtDate(s.started_at)}</td><td className="px-3 py-2">{fmtDate(s.expires_at)}</td><td className="px-3 py-2">{s.period_count}</td></tr>))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-zinc-500">None.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}

function Reconciliation() {
  const [d, setD] = useState(null);
  const load = () => client.get("/admin/billing/exceptions").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const runNow = async () => { await client.post("/admin/billing/reconcile"); toast.success("Reconciliation started"); setTimeout(load, 3000); };
  if (!d) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="billing-reconciliation">
      <div>
        <div className="flex justify-between items-center mb-3"><h3 className="font-display font-bold">Reconciliation runs</h3>
          <button data-testid="reconcile-now" onClick={runNow} className="px-4 py-1.5 rounded-full bg-[#ccff00] text-black text-xs font-semibold">Run now</button></div>
        {d.runs.map((r) => <p key={r.id} className="text-xs text-zinc-400 border-b border-white/5 py-1.5">{fmtDate(r.created_at)} · {r.trigger} · {r.status} · checked {r.checked ?? "-"} · updated {r.updated ?? "-"} · reminders {r.reminders ?? "-"} · errors {r.errors ?? "-"}</p>)}
      </div>
      <div>
        <h3 className="font-display font-bold mb-3">Exceptions & refund cases</h3>
        {d.exceptions.map((e) => <p key={e.id} className="text-xs border-b border-white/5 py-1.5"><span className={e.status === "open" ? "text-amber-300" : "text-zinc-500"}>{e.status}</span> · {e.kind} · <span className="font-mono-cl">{e.ref}</span> · {e.detail}</p>)}
        {d.exceptions.length === 0 && <p className="text-sm text-zinc-500">No exceptions.</p>}
      </div>
    </div>
  );
}

function ProviderSettings() {
  const [d, setD] = useState(null);
  const [s, setS] = useState(null);
  const [reason, setReason] = useState("");
  const [run, dialog] = useGuarded();
  const load = () => client.get("/admin/billing/settings").then((r) => { setD(r.data); setS(r.data.settings); });
  useEffect(() => { load(); }, []);
  if (!d || !s) return null;
  const p = d.provider;
  const save = () => run(async () => { const r = await client.put("/admin/billing/settings", { ...s, reason }); toast.success(`Saved. USDT mode: ${r.data.mode}`); load(); });
  const check = () => run(async () => { const r = await client.post("/admin/billing/provider/check"); toast.success(`Provider ${r.data.api_status}`); load(); });
  const T = ({ k, label, hint }) => (
    <div className="flex justify-between items-start gap-4 py-3 border-b border-white/5">
      <div><p className="text-sm font-medium">{label}</p>{hint && <p className="text-xs text-zinc-500">{hint}</p>}</div>
      <Switch data-testid={`setting-${k}`} checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} />
    </div>
  );
  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="billing-settings">
      <div>
        <h3 className="font-display font-bold mb-2">Payment methods</h3>
        <p className="text-xs text-zinc-500 mb-2">Current USDT mode: <b data-testid="usdt-mode" className="text-zinc-200 uppercase">{d.mode}</b></p>
        <T k="billing_enabled" label="Billing enabled" hint="Master switch for all checkouts" />
        <T k="usdt_enabled" label="USDT via NOWPayments" />
        <T k="midtrans_enabled" label="QRIS via Midtrans (Rupiah)" hint="Existing analyst subscriptions" />
        <T k="live_payments_enabled" label="Live USDT payments" hint={d.env.live_gate ? "Requires re-authentication and a reason" : "Locked by server: BILLING_LIVE_PAYMENTS_ENABLED=false"} />
        <p className="text-sm font-medium mt-4 mb-2">Accepted USDT networks</p>
        {Object.entries(d.networks).map(([code, name]) => {
          const verified = p.merchant_usdt.includes(code), allowed = p.allowed_networks.includes(code);
          return (
            <label key={code} className="flex items-center gap-3 text-sm py-1">
              <input type="checkbox" data-testid={`setting-net-${code}`} disabled={!allowed} checked={s.networks.includes(code)}
                onChange={(e) => setS({ ...s, networks: e.target.checked ? [...s.networks, code] : s.networks.filter((n) => n !== code) })} />
              USDT · {name} <span className={`text-[10px] ${verified ? "text-[#ccff00]" : "text-zinc-500"}`}>{verified ? "merchant-verified" : allowed ? "not verified yet" : "not allowed by server"}</span>
            </label>);
        })}
        <p className="text-[11px] text-zinc-600 mt-1">USDT on Base is not offered by NOWPayments.</p>
        <input data-testid="settings-reason" className={inputCls + " w-full mt-4"} placeholder="Reason for change (required for live)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button data-testid="settings-save" onClick={save} className="mt-3 px-6 py-2.5 rounded-full bg-[#ccff00] text-black text-sm font-semibold">Save settings</button>
      </div>
      <div>
        <h3 className="font-display font-bold mb-2">Integration: {p.name}</h3>
        {[["Configuration", p.configured ? "Configured" : "Missing credentials"], ["API key", p.api_key || "-"], ["IPN secret", p.ipn_secret],
          ["Environment", p.environment], ["Custody API", p.custody_enabled ? "Enabled" : "Not connected"], ["Payouts", "Disabled (V1 policy)"],
          ["Webhook URL", p.webhook_url], ["Last provider contact", fmtDate(p.last_ok_at)], ["Last webhook", fmtDate(p.last_webhook_at)],
          ["Last reconciliation", fmtDate(p.last_reconciliation_at)], ["Merchant USDT networks", p.merchant_usdt.join(", ") || "not checked"]].map(([k, v]) => (
          <p key={k} className="text-sm flex justify-between gap-4 py-1.5 border-b border-white/5"><span className="text-zinc-500">{k}</span><span className="text-right break-all" data-testid={`provider-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</span></p>))}
        <button data-testid="provider-check" onClick={check} className="mt-4 px-5 py-2 rounded-full border border-white/15 text-sm">Check connection</button>
        <p className="text-[11px] text-zinc-600 mt-3">Secrets live only in server environment variables. They can't be viewed or edited here.</p>
      </div>
      {dialog}
    </div>
  );
}

function Audit({ domain = "billing" }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get("/admin/billing/audit", { params: { domain } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, [domain]);
  return (
    <div data-testid={`${domain}-audit`}>
      {rows.map((l) => <p key={l.id} className="text-xs text-zinc-400 border-b border-white/5 py-1.5">{fmtDate(l.created_at)} · <b className="text-zinc-200">{l.action}</b> · {l.resource} · {l.actor_email}{l.reason ? ` · "${l.reason}"` : ""} · <span className="font-mono-cl text-zinc-600">{l.correlation_id?.slice(0, 8)}</span></p>)}
      {rows.length === 0 && <p className="text-sm text-zinc-500">No entries yet.</p>}
    </div>
  );
}

export { Audit };

export function BillingCenter() {
  const [s, setS] = useState("overview");
  const items = [["overview", "Overview"], ["products", "Products & Pricing"], ["orders", "Orders & Payments"], ["subscribers", "Subscribers"],
    ["renewals", "Renewals"], ["reviews", "Refunds & Reconciliation"], ["settings", "Provider Settings"], ["audit", "Billing Audit Logs"]];
  return (
    <div data-testid="billing-center">
      <h2 className="font-display text-2xl font-bold mb-4">Billing</h2>
      <SubNav items={items} value={s} onChange={setS} prefix="billing-nav" />
      {s === "overview" && <Overview />}
      {s === "products" && <ProductsManager />}
      {s === "orders" && <Orders />}
      {s === "subscribers" && <Subscribers />}
      {s === "renewals" && <Subscribers expiring />}
      {s === "reviews" && <><Reconciliation /><div className="mt-8"><h3 className="font-display font-bold mb-3">Orders under review</h3><Orders preset={{ status: "REVIEW_REQUIRED" }} /></div></>}
      {s === "settings" && <ProviderSettings />}
      {s === "audit" && <Audit domain="billing" />}
    </div>
  );
}
